package br.com.htms.inspeccaocampo.data.epi

import br.com.htms.inspeccaocampo.data.os.PgrCatalog

/** Marca/CA de um item da relação de EPI; [validade] em dd/mm/aaaa (vazio = não informado). */
data class EpiCa(val marca: String, val ca: String, val validade: String)

data class EpiRelacaoItem(val item: String, val opcoes: List<EpiCa>)

/** Legenda da matriz: X, XX e XXX. */
enum class EpiUso(val sigla: String, val rotulo: String) {
    BASICO("X", "Uso básico da função"),
    AREA("XX", "Ao entrar em área de exposição ao risco"),
    ATIVIDADE("XXX", "Quando necessário para a atividade")
}

/** Coluna da matriz por função, com o item correspondente na relação de EPI (CA). */
data class EpiColuna(
    val nome: String,
    val descricaoFicha: String,
    val quantidade: String,
    val relacaoChave: String?
)

data class EpiMatrizFuncao(val setor: String, val funcao: String, val usos: List<Pair<EpiColuna, EpiUso>>)

object EpiCatalog {
    val fonte: String get() = EPI_MATRIZ_FONTE

    /** Seed + overrides salvos na Ficha de EPI (quando houver). */
    private var relacaoRuntime: List<EpiRelacaoItem> = EPI_RELACAO

    val relacao: List<EpiRelacaoItem> get() = relacaoRuntime

    fun definirRelacao(lista: List<EpiRelacaoItem>) {
        relacaoRuntime = lista.ifEmpty { EPI_RELACAO }
    }

    fun seedRelacao(): List<EpiRelacaoItem> = EPI_RELACAO

    /** Mesma ordem das colunas B..S da aba MATRIZ. */
    val colunas: List<EpiColuna> = listOf(
        EpiColuna("Capacete de Segurança", "Capacete de segurança", "1 uni", "capacete"),
        EpiColuna("Óculos de Segurança", "Óculos de proteção", "1 uni", "oculos danny aguia incolor"),
        EpiColuna("Máscara de proteção PFF2", "Máscara de proteção PFF2", "1 uni", "pff2"),
        EpiColuna("Protetor Facial", "Protetor facial", "1 uni", "kit prot facial"),
        EpiColuna("Luva de PU", "Luva de PU", "1 par", "luva pu"),
        EpiColuna("Luva em Raspa", "Luva em raspa", "1 par", "luva de raspa"),
        EpiColuna("Luva Nitrílica", "Luva nitrílica", "1 par", "luva nitrilica"),
        EpiColuna("Luva Tricotada", "Luva contra agentes mecânicos", "1 par", "luva contra agentes"),
        EpiColuna("Luva de Eletricista", "Luva de eletricista", "1 par", "luva eletricista"),
        EpiColuna("Protetor auricular plug", "Protetor auricular", "1 uni", "prot plug"),
        EpiColuna("Bala clava", "Touca tipo árabe (Anti-Chamas)", "1 uni", "balaclava"),
        EpiColuna("Macacão Tyvek", "Macacão tipo Tyvek", "1 uni", "macacao"),
        EpiColuna("Calçado em PVC", "Bota de PVC", "1 par", "bota pvc"),
        EpiColuna("Calçado de Segurança em couro", "Calçado de segurança para eletricista", "1 par", "botina"),
        EpiColuna("Avental em Raspa", "Avental em raspa", "1 uni", "avental"),
        EpiColuna("Perneira", "Perneira", "1 par", "perneira"),
        EpiColuna("Cinto de Segurança", "Cinturão de segurança", "1 uni", "cinturao"),
        EpiColuna("Uniforme RF", "Camisa retardante de chamas", "1 uni", "camisa retardante")
    )

    val funcoes: List<EpiMatrizFuncao> by lazy {
        EPI_MATRIZ_LINHAS.map { (setor, funcao, codigos) ->
            val usos = colunas.mapIndexedNotNull { i, col ->
                when (codigos.getOrNull(i)) {
                    '1' -> col to EpiUso.BASICO
                    '2' -> col to EpiUso.AREA
                    '3' -> col to EpiUso.ATIVIDADE
                    else -> null
                }
            }
            EpiMatrizFuncao(setor.trim(), funcao.trim(), usos)
        }
    }

    private fun chave(s: String): String =
        PgrCatalog.normalizar(s).map { if (it.isLetterOrDigit()) it else ' ' }.joinToString("")
            .replace(Regex("\\s+"), " ").trim()
            .replace("ofical", "oficial")

    /**
     * Linha da matriz para o cargo do PGR. O GHE do PGR e da matriz divergem a partir do 17,
     * por isso casa por nome do setor + função, depois só pela função.
     */
    fun encontrarFuncao(setorNome: String, cargoNome: String): EpiMatrizFuncao? {
        val s = chave(setorNome)
        val c = chave(cargoNome)
        if (c.isEmpty()) return null
        val mesmoSetor = funcoes.filter { chave(it.setor) == s }
        fun casar(lista: List<EpiMatrizFuncao>): EpiMatrizFuncao? =
            lista.firstOrNull { chave(it.funcao) == c }
                ?: lista.firstOrNull { val f = chave(it.funcao); c.startsWith("$f ") || f.startsWith("$c ") }
        return casar(mesmoSetor) ?: casar(funcoes)
    }

    fun relacaoDe(coluna: EpiColuna): EpiRelacaoItem? {
        val k = coluna.relacaoChave ?: return null
        return relacao.firstOrNull { k in chave(it.item) }
    }

    /** yyyymmdd para comparar datas dd/mm/aaaa; null se inválida. */
    fun dataOrdenavel(br: String): Int? {
        val p = br.trim().split('/')
        if (p.size != 3) return null
        val d = p[0].toIntOrNull() ?: return null
        val m = p[1].toIntOrNull() ?: return null
        val a = p[2].toIntOrNull() ?: return null
        if (d !in 1..31 || m !in 1..12 || a < 1900) return null
        return a * 10000 + m * 100 + d
    }

    /** true/false conforme a validade do CA na data de entrega; null se sem validade ou data inválida. */
    fun caValidoEm(ca: EpiCa, dataEntrega: String): Boolean? {
        val v = dataOrdenavel(ca.validade) ?: return null
        val e = dataOrdenavel(dataEntrega) ?: return null
        return v >= e
    }

    /** Primeira opção com CA válido na data; senão a primeira com CA; senão a primeira. */
    fun melhorOpcao(item: EpiRelacaoItem, dataEntrega: String): Int {
        val idx = item.opcoes.indices
        return idx.firstOrNull { item.opcoes[it].ca.isNotBlank() && caValidoEm(item.opcoes[it], dataEntrega) != false }
            ?: idx.firstOrNull { item.opcoes[it].ca.isNotBlank() }
            ?: 0
    }
}

data class FichaEpiLinha(val quantidade: String, val ca: String, val descricao: String)

data class FichaEpiDados(
    val nome: String,
    val cpf: String,
    val matricula: String,
    val funcao: String,
    val setor: String,
    val admissao: String,
    val empresa: String,
    val dataEntrega: String,
    val linhas: List<FichaEpiLinha>
)

/** Persistência local da relação EPI/CA editável na Ficha de EPI. */
object EpiRelacaoJson {
    fun toJson(lista: List<EpiRelacaoItem>): String = buildString {
        append('[')
        lista.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append('{')
            append("\"item\":").append(str(item.item)).append(',')
            append("\"opcoes\":[")
            item.opcoes.forEachIndexed { j, op ->
                if (j > 0) append(',')
                append('{')
                append("\"marca\":").append(str(op.marca)).append(',')
                append("\"ca\":").append(str(op.ca)).append(',')
                append("\"validade\":").append(str(op.validade))
                append('}')
            }
            append("]}")
        }
        append(']')
    }

    fun fromJson(raw: String): List<EpiRelacaoItem>? = runCatching {
        val t = raw.trim()
        if (!t.startsWith("[")) return@runCatching null
        val itens = mutableListOf<EpiRelacaoItem>()
        var i = 1
        while (i < t.length) {
            while (i < t.length && t[i] in " \n\r\t,") i++
            if (i >= t.length || t[i] == ']') break
            if (t[i] != '{') return@runCatching null
            val (obj, next) = objeto(t, i)
            i = next
            val nome = obj["item"] ?: continue
            val opsRaw = obj["opcoes"] ?: "[]"
            val ops = parseOpcoes(opsRaw)
            itens += EpiRelacaoItem(nome, ops.ifEmpty { listOf(EpiCa("", "", "")) })
        }
        itens
    }.getOrNull()

    private fun parseOpcoes(raw: String): List<EpiCa> {
        val t = raw.trim()
        if (!t.startsWith("[")) return emptyList()
        val out = mutableListOf<EpiCa>()
        var i = 1
        while (i < t.length) {
            while (i < t.length && t[i] in " \n\r\t,") i++
            if (i >= t.length || t[i] == ']') break
            if (t[i] != '{') break
            val (obj, next) = objeto(t, i)
            i = next
            out += EpiCa(obj["marca"].orEmpty(), obj["ca"].orEmpty(), obj["validade"].orEmpty())
        }
        return out
    }

    private fun objeto(s: String, start: Int): Pair<Map<String, String>, Int> {
        var i = start + 1
        val map = linkedMapOf<String, String>()
        while (i < s.length && s[i] != '}') {
            while (i < s.length && s[i] in " \n\r\t,") i++
            if (i >= s.length || s[i] == '}') break
            require(s[i] == '"')
            val (key, i2) = string(s, i)
            i = i2
            while (i < s.length && s[i] in " \n\r\t:") i++
            when {
                i < s.length && s[i] == '"' -> {
                    val (v, i3) = string(s, i)
                    map[key] = v
                    i = i3
                }
                i < s.length && s[i] == '[' -> {
                    val end = fechaArray(s, i)
                    map[key] = s.substring(i, end)
                    i = end
                }
                else -> break
            }
        }
        if (i < s.length && s[i] == '}') i++
        return map to i
    }

    private fun fechaArray(s: String, start: Int): Int {
        var depth = 0
        var i = start
        var inStr = false
        var esc = false
        while (i < s.length) {
            val c = s[i++]
            if (inStr) {
                when {
                    esc -> esc = false
                    c == '\\' -> esc = true
                    c == '"' -> inStr = false
                }
            } else when (c) {
                '"' -> inStr = true
                '[' -> depth++
                ']' -> {
                    depth--
                    if (depth == 0) return i
                }
            }
        }
        return s.length
    }

    private fun string(s: String, start: Int): Pair<String, Int> {
        require(s[start] == '"')
        val sb = StringBuilder()
        var i = start + 1
        while (i < s.length) {
            val c = s[i++]
            if (c == '\\' && i < s.length) {
                when (val n = s[i++]) {
                    'n' -> sb.append('\n')
                    'r' -> sb.append('\r')
                    't' -> sb.append('\t')
                    '"' -> sb.append('"')
                    '\\' -> sb.append('\\')
                    else -> sb.append(n)
                }
            } else if (c == '"') {
                return sb.toString() to i
            } else sb.append(c)
        }
        return sb.toString() to i
    }

    private fun str(v: String): String = buildString {
        append('"')
        v.forEach { c ->
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> append(c)
            }
        }
        append('"')
    }
}
